import { type Dispatch, type SetStateAction, useState } from 'react'

import { CopyButton, type HeadItemType, Status, type StatusType, Table } from 'components'
import { URL_CONST } from 'constants/urls'
import {
  type DraftProps,
  StatusOV,
  type StatusOVValues,
} from 'container/Home/components/Banners/interface'
import CheckedOVsVerification from 'container/ManageOrders/components/CheckedOVsVerification'
import OvMenu from 'container/ManageOrders/components/OvMenu'
import {
  type GetNegDif,
  getEDIRowIcon,
  getHavaRowIcon,
  getNegDifRowIcon,
} from 'container/ManageOrders/utils'
import SapModal, { type SapModalTypes } from 'container/ManageOVDetails/components/SapModal'
import type { SapOptionsToOvStatus } from 'store/OVContext/ovContextTypes'
import { asyncSetOvSap, setCheckedOvs } from 'store/SalesOrderContext/salesOrderSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

type ViewByOvProps = {
  draftsData: DraftProps[]
  setDraftsData: Dispatch<SetStateAction<DraftProps[]>>
  ovsSapData: SapOptionsToOvStatus
  setOvsSapData: Dispatch<SetStateAction<SapOptionsToOvStatus>>
}
const ViewByOv = ({ draftsData, setOvsSapData, setDraftsData, ovsSapData }: ViewByOvProps) => {
  const { checkedOvs } = useSelector(({ salesOrder }) => salesOrder)
  const [sapModal, setSapModal] = useState<SapModalTypes | undefined>()

  const dispatch = useDispatch()

  const headTable: HeadItemType[] = [
    {
      columnName: 'Status',
      label: 'Status',
    },
    {
      columnName: 'NumeroPedidoRascunho',
      label: 'Pedido Portal',
    },
    {
      columnName: 'NumeroPedidoCliente',
      label: 'Pedido Cliente',
    },
    {
      columnName: 'RazaoSocialCliente',
      label: 'Razão social',
    },
    {
      columnName: 'NumeroOrdemVenda',
      label: 'Ordem de Venda',
    },
    {
      columnName: 'DataEntregaCalculada',
      label: 'Datas',
    },
    {
      columnName: 'Fabrica',
      label: 'Fábrica',
    },
    {
      columnName: '',
      label: '',
    },
  ]

  const toggleCheckOv = (isChecked: boolean, id?: string) => {
    if (id) {
      const updatedOvsData = draftsData.map((ov) =>
        ov.NumeroOrdemVenda === id ? { ...ov, checked: isChecked } : ov,
      )
      const updatedCheckedOvs = updatedOvsData.filter((ov) => ov.checked)
      dispatch(setCheckedOvs(updatedCheckedOvs))
      setDraftsData(updatedOvsData)
    } else {
      const updatedOvsData = draftsData.map((ov) => ({ ...ov, checked: isChecked }))
      const updatedCheckedOvs = updatedOvsData.filter((ov) => ov.checked)
      dispatch(setCheckedOvs(updatedCheckedOvs))
      setDraftsData(updatedOvsData)
    }
  }

  const handleOvsSAP = () => {
    dispatch(
      asyncSetOvSap({
        body: ovsSapData.body,
        type: ovsSapData.type,
      }),
    )
  }

  return (
    <>
      {!!checkedOvs.length && (
        <CheckedOVsVerification
          ovsSapData={ovsSapData}
          setOvsSapData={setOvsSapData}
          setSapModal={setSapModal}
        />
      )}
      <Table
        bodyData={draftsData.map((draft, index) => {
          const label: StatusOVValues = StatusOV[draft.StatusOrdemVenda]
          const statusType = label.split(' - ')[0] as StatusType
          const ovsNegDifInfo: GetNegDif = {
            flag: draft.FlagNegociacaoDiferenciada,
            isJustified: draft.DataJustificativa,
            ovStatus: +draft.StatusOrdemVenda.split('-')[0],
          }
          return {
            checked: draft.checked,
            colunsNotNavigate: [7],
            content: [
              <S.StatusWrapper key={`status-${draft.NumeroOrdemVenda}+${index}`}>
                <Status label={label} status={statusType} />
              </S.StatusWrapper>,
              <S.TableInfo key={`number-${draft.NumeroPedidoRascunho}+${index}`}>
                <S.CellItem>
                  {!!draft.NumeroPedidoRascunho && (
                    <>
                      {draft.NumeroPedidoRascunho}
                      <CopyButton text={draft.NumeroPedidoRascunho} />
                    </>
                  )}
                  {getNegDifRowIcon([ovsNegDifInfo])}
                  {getHavaRowIcon(draft.Origem)}
                  {getEDIRowIcon(draft.Origem, undefined, true, draft.FlagEdiInternacional)}
                </S.CellItem>
                <span>Solicitado em {draft.DataSolicitacaoPedido}</span>
              </S.TableInfo>,
              <S.TableInfo key="number-client-order">
                <S.CellItem>
                  {draft.NumeroPedidoCliente || (
                    <S.UnavailableInfo>Não informado</S.UnavailableInfo>
                  )}
                </S.CellItem>
              </S.TableInfo>,
              <S.TableInfo key={`client-${draft.NumeroPedidoRascunho}+${index}`}>
                {draft.RazaoSocialCliente}
                <span>Código SAP: {draft.CodigoClienteSap}</span>
              </S.TableInfo>,
              <S.TableInfo key={`client-${draft.NumeroOrdemVenda}+${index}`}>
                <S.CellItem>
                  {draft.NumeroOrdemVenda}
                  <CopyButton text={draft.NumeroOrdemVenda} />
                </S.CellItem>
                <S.CellItem>
                  <span>SAP: {draft.NumeroOrdemVendaSap ?? '--'}</span>
                  {draft.NumeroOrdemVendaSap && <CopyButton text={draft.NumeroOrdemVendaSap} />}
                </S.CellItem>
              </S.TableInfo>,
              <S.TableInfo key={`client-${draft.NumeroPedidoRascunho}+${index}`}>
                Entrega: {draft.DataEntregaCalculada}
                <span>Faturamento: {draft.DataFaturamentoPrevisto}</span>
              </S.TableInfo>,
              <S.FabInfo key={`${draft.NumeroOrdemVenda} + ${index}`}>
                {draft.CodigoFabrica}
              </S.FabInfo>,
              <S.OvMenu key={`${draft.NumeroOrdemVenda} + ${index}`}>
                <OvMenu
                  ovData={draft}
                  setCurrentModal={setSapModal}
                  setOvsSapData={setOvsSapData}
                />
              </S.OvMenu>,
            ],
            id: `${draft.NumeroOrdemVenda}`,
            linkNavigate: `${URL_CONST.MANAGE_OV}/${draft.NumeroOrdemVenda}`,
          }
        })}
        hasCheckbox
        headData={headTable}
        setChecked={toggleCheckOv}
      />
      <SapModal handleButtonAction={handleOvsSAP} sapModal={sapModal} setSapModal={setSapModal} />
    </>
  )
}

export default ViewByOv
